import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ShieldX, IdCard } from "lucide-react";
import { toast } from "sonner";

import { EmptyState } from "@/components/empty-state";
import { SectionHeading } from "@/components/section-heading";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { decideAgeVerification, listAgeVerifications } from "@/lib/age-verification.functions";

export const Route = createFileRoute("/_authenticated/admin/moderation/age-verification")({
  head: () => ({
    meta: [
      { title: "Age verification queue — Quibblies" },
      {
        name: "description",
        content:
          "Administrator queue for reviewing government-issued ID photos submitted as proof of age.",
      },
      { property: "og:title", content: "Age verification queue — Quibblies" },
      { property: "og:description", content: "Approve or reject submitted proof of age." },
      { property: "og:type", content: "website" },
      { name: "robots", content: "noindex" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AgeVerificationQueue,
});

function AgeVerificationQueue() {
  const queryClient = useQueryClient();
  const load = useServerFn(listAgeVerifications);
  const decide = useServerFn(decideAgeVerification);
  const [status, setStatus] = React.useState<"pending" | "approved" | "rejected">("pending");
  const [notes, setNotes] = React.useState<Record<string, string>>({});
  const [expiries, setExpiries] = React.useState<Record<string, string>>({});

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-age-verifications", status],
    queryFn: () => load({ data: { status } }),
    retry: false,
  });

  const mutation = useMutation({
    mutationFn: (input: {
      id: string;
      decision: "approved" | "rejected";
      note: string;
      expiresAt: string | null;
    }) => decide({ data: input }),
    onSuccess: (_r, input) => {
      toast.success(input.decision === "approved" ? "Age verified" : "Proof rejected");
      void queryClient.invalidateQueries({ queryKey: ["admin-age-verifications"] });
    },
    onError: (e: Error) => toast.error("Could not save the decision", { description: e.message }),
  });

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
      <SectionHeading
        title="Age verification"
        description="Photo ID submitted as proof of age. Files are private, links expire after ten minutes, and the file is deleted from storage once you decide."
      />

      <Tabs value={status} onValueChange={(v) => setStatus(v as typeof status)} className="mt-6">
        <TabsList>
          <TabsTrigger value="pending">Awaiting review</TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
        </TabsList>
      </Tabs>

      {isLoading ? (
        <Skeleton className="mt-8 h-64 w-full rounded-2xl" />
      ) : error ? (
        <div className="mt-8">
          <EmptyState
            icon={ShieldX}
            title="Administrator access required"
            description="Age verification is restricted to Administrators and the Owner."
          />
        </div>
      ) : !data?.length ? (
        <div className="mt-8">
          <EmptyState
            icon={IdCard}
            title="Nothing here"
            description="No age verification submissions in this state right now."
          />
        </div>
      ) : (
        <ul className="mt-8 space-y-4">
          {data.map((row) => (
            <li key={row.id} className="rounded-2xl border border-border/60 bg-surface/50 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-foreground">
                  @{row.username}{" "}
                  <span className="text-muted-foreground">({row.display_name})</span>
                </p>
                <Badge variant="secondary">
                  Submitted {new Date(row.created_at).toLocaleDateString()}
                </Badge>
              </div>

              {row.url ? (
                <div className="mt-3 overflow-hidden rounded-xl border border-border/60 bg-background/40">
                  <img
                    src={row.url}
                    alt={`Proof of age submitted by @${row.username}`}
                    className="mx-auto max-h-80 object-contain"
                    loading="lazy"
                  />
                </div>
              ) : (
                <p className="mt-3 rounded-xl border border-border/60 bg-background/40 p-6 text-center text-xs text-muted-foreground">
                  File no longer stored (deleted after review).
                </p>
              )}

              {row.status === "pending" ? (
                <div className="mt-4 space-y-3">
                  <Textarea
                    placeholder="Reviewer note (shared with the member)"
                    value={notes[row.id] ?? ""}
                    onChange={(e) => setNotes((n) => ({ ...n, [row.id]: e.target.value }))}
                    rows={2}
                  />
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="text-xs text-muted-foreground">
                      Expires (optional)
                      <Input
                        type="date"
                        className="mt-1"
                        value={expiries[row.id] ?? ""}
                        onChange={(e) =>
                          setExpiries((x) => ({ ...x, [row.id]: e.target.value }))
                        }
                      />
                    </label>
                    <div className="ml-auto flex gap-2">
                      <Button
                        variant="outline"
                        disabled={mutation.isPending}
                        onClick={() =>
                          mutation.mutate({
                            id: row.id,
                            decision: "rejected",
                            note: notes[row.id] ?? "",
                            expiresAt: null,
                          })
                        }
                      >
                        Reject
                      </Button>
                      <Button
                        disabled={mutation.isPending}
                        onClick={() =>
                          mutation.mutate({
                            id: row.id,
                            decision: "approved",
                            note: notes[row.id] ?? "",
                            expiresAt: expiries[row.id]
                              ? new Date(`${expiries[row.id]}T00:00:00Z`).toISOString()
                              : null,
                          })
                        }
                      >
                        Approve
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                  <p>
                    {row.status === "approved" ? "Approved" : "Rejected"}
                    {row.reviewed_at
                      ? ` on ${new Date(row.reviewed_at).toLocaleString()}`
                      : ""}
                    {row.expires_at
                      ? ` · expires ${new Date(row.expires_at).toLocaleDateString()}`
                      : ""}
                  </p>
                  {row.reviewer_note ? <p>Note: {row.reviewer_note}</p> : null}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
